import type { DateTime } from 'luxon';
import { DateTime as LuxonDateTime } from 'luxon';
import { BsCalendar3, BsChat } from 'react-icons/bs';
import { HiOutlineUser } from 'react-icons/hi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TIME_ZONE_UTC } from '@/constants';
import { useDeleteEventMessage } from '@/features/events/components/EventModal/components/EventMessages/hooks/useDeleteEventMessage';
import { TEXTS_QUERY_PARAM } from '@/features/events/components/EventModal/components/EventMessages/hooks/useEventMessagesPanelState';
import { EVENT_QUERY_PARAM } from '@/features/events/hooks/useEditEventModal';
import { serializeEventKey } from '@/features/events/lib/eventInstance.lib';
import { useQueryParams } from '@/hooks/useQueryParams';
import { summarizeMessageSendDate } from '@/pages/Schedule/utils';
import { colors } from '@/theme';
import { MessageStatusType } from '@/types/gql.generated';
import { Badge, Box, Button, Flex, type FlexProps, useDisclosure } from '@/ui';
import type { ExperienceEventMessage } from './hooks/useExperienceEventMessages';

const STATUS_COLOR_SCHEMES: Partial<Record<MessageStatusType, string>> = {
  [MessageStatusType.Scheduled]: 'yellow',
  [MessageStatusType.Sent]: 'green',
  [MessageStatusType.Failed]: 'red',
};

// Labelled from the message's own instance, not event.start — for a repeating
// event that is the series start, so every instance's message would read the
// same date. All-day instances are stored as a bare calendar date, so they are
// parsed in UTC to keep negative-offset viewers off the previous day.
const instanceDateLabel = (
  message: ExperienceEventMessage,
  displayTimezone: string
) =>
  message.event.isAllDay
    ? LuxonDateTime.fromISO(message.instance, {
        zone: TIME_ZONE_UTC,
      }).toFormat('MMM d, yyyy')
    : LuxonDateTime.fromISO(message.instance)
        .setZone(message.event.start.timeZone ?? displayTimezone)
        .toFormat('MMM d, yyyy');

type Props = FlexProps & {
  message: ExperienceEventMessage;
  sendAt: DateTime;
  displayTimezone: string;
  /** The All view badges every status; Scheduled/Sent only badge failures. */
  showStatus?: boolean;
};

export const MessageRow = ({
  message,
  sendAt,
  displayTimezone,
  showStatus,
  ...props
}: Props) => {
  const { event } = message;
  const { setQueryParams } = useQueryParams();
  const { deleteEventMessage } = useDeleteEventMessage(event.id);
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: openDeleteConfirm,
    onClose: closeDeleteConfirm,
  } = useDisclosure();

  const canModifyMessage = message.status === MessageStatusType.Scheduled;

  // Editing happens in the event modal's Texts sidebar — deep link to it,
  // scoped to this message's instance. EventMessageEvent carries no rrule, so
  // the instance is always appended and useQueryEvent drops it for single events.
  const openEventTexts = () =>
    setQueryParams({
      [EVENT_QUERY_PARAM]: serializeEventKey(
        event.id,
        LuxonDateTime.fromISO(message.instance, { zone: TIME_ZONE_UTC })
      ),
      [TEXTS_QUERY_PARAM]: '1',
    });

  const recipientNames =
    message.recipients.length === 0
      ? 'Everyone'
      : message.recipients.length === 2
        ? message.recipients.map(({ displayName }) => displayName).join(' and ')
        : message.recipients.map(({ displayName }) => displayName).join(', ');

  const eventTitle = [event.emoji, event.title].filter(Boolean).join(' ');

  return (
    <>
      <ConfirmDialog
        confirmButtonText="Delete"
        isOpen={isDeleteConfirmOpen}
        message={`"${message.body}"`}
        title="Delete this message?"
        onCancel={closeDeleteConfirm}
        onConfirm={() => {
          deleteEventMessage(message.id, {
            onSuccess: closeDeleteConfirm,
          });
        }}
      />

      <Flex
        {...props}
        data-event-id={event.id}
        data-message-id={message.id}
        direction={{ base: 'column', md: 'row' }}
      >
        <Box flex="1" mr={{ base: 0, md: 10 }}>
          <Box
            bg="customgray.dark"
            borderRadius="25px 25px 0 25px"
            color="gray.100"
            overflowWrap="anywhere"
            p="8"
            whiteSpace="pre-wrap"
          >
            {message.body}
          </Box>
          <Flex align="center" mt="3">
            {message.status &&
              (showStatus || message.status === MessageStatusType.Failed) && (
                <Badge
                  colorScheme={STATUS_COLOR_SCHEMES[message.status] ?? 'blue'}
                  mr="3"
                >
                  {message.status}
                </Badge>
              )}
            {canModifyMessage && (
              <>
                <Button
                  color="customgray.mid"
                  colorScheme="dark"
                  mr="3"
                  size="sm"
                  variant="link"
                  onClick={openEventTexts}
                >
                  Edit
                </Button>
                <Button
                  color="customgray.mid"
                  colorScheme="dark"
                  size="sm"
                  variant="link"
                  onClick={openDeleteConfirm}
                >
                  Delete
                </Button>
              </>
            )}
          </Flex>
        </Box>

        <Box
          ml={{ base: 2, md: 0 }}
          mt={{ base: 3, md: 0 }}
          w={{ base: 'auto', md: '250px' }}
        >
          <Flex align="flex-start" fontSize="sm">
            <Box mr="5" mt="3px">
              <BsCalendar3 color={colors.customgray.mid} size="14" />
            </Box>
            {eventTitle} on {instanceDateLabel(message, displayTimezone)}
          </Flex>

          <Flex align="flex-start" fontSize="sm" mt="2">
            <Box mr="5" mt="3px">
              <BsChat color={colors.customgray.mid} size="14" />
            </Box>
            <Box>{summarizeMessageSendDate({ ...message, sendAt })}</Box>
          </Flex>

          <Flex align="flex-start" fontSize="sm" mt="2">
            <Box mr="5" mt="3px">
              <HiOutlineUser color={colors.customgray.mid} size="14" />
            </Box>
            {recipientNames}
          </Flex>
        </Box>
      </Flex>
    </>
  );
};
